import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, AlertCircle, Mail } from 'lucide-react';
import { AuthLayout } from '@/components/auth/AuthLayout';
import { verifyEmail, resendVerification, readApiError } from '@/lib/auth-api';

type State = 'pending' | 'verifying' | 'success' | 'error' | 'awaiting';

export function VerifyEmailPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token');
  const email = params.get('email');
  const [state, setState] = useState<State>(token ? 'verifying' : 'awaiting');
  const [error, setError] = useState<string | null>(null);
  const [resent, setResent] = useState(false);
  const [resending, setResending] = useState(false);

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        await verifyEmail(token);
        setState('success');
        setTimeout(() => navigate('/login', { replace: true }), 1500);
      } catch (err) {
        setState('error');
        setError(readApiError(err, t('auth.error_verify_failed')));
      }
    })();
  }, [token, navigate, t]);

  async function handleResend() {
    setResending(true);
    setError(null);
    try {
      await resendVerification();
      setResent(true);
    } catch (err) {
      setError(readApiError(err, t('auth.error_resend_failed')));
    } finally {
      setResending(false);
    }
  }

  if (state === 'verifying') {
    return (
      <AuthLayout title={t('auth.verify_loading_title')} subtitle={t('auth.verify_loading_subtitle')}>
        <div className="rounded-2xl bg-ink-100 border border-ink-200 px-4 py-5 flex items-center gap-3">
          <span className="h-4 w-4 rounded-full border-2 border-accent-500/30 border-t-accent-500 animate-spin" />
          <p className="text-sm text-ink-700">{t('auth.verify_loading_msg')}</p>
        </div>
      </AuthLayout>
    );
  }

  if (state === 'success') {
    return (
      <AuthLayout title={t('auth.verify_success_title')} subtitle={t('auth.verify_success_subtitle')}>
        <div className="rounded-2xl bg-emerald-50 border border-emerald-200 px-4 py-5 flex items-start gap-3">
          <CheckCircle2 className="h-5 w-5 text-emerald-600 mt-0.5 shrink-0" />
          <p className="text-sm text-emerald-900">{t('auth.verify_redirecting')}</p>
        </div>
      </AuthLayout>
    );
  }

  if (state === 'error') {
    return (
      <AuthLayout
        title={t('auth.verify_failed_title')}
        subtitle={t('auth.verify_failed_subtitle')}
        footer={
          <>
            {t('auth.contact_support_prefix')}{' '}
            <a href="mailto:support@patronez.com" className="font-semibold text-accent-700 hover:underline">
              support@patronez.com
            </a>
          </>
        }
      >
        <div className="rounded-2xl bg-warn-50 border border-warn-500/40 px-4 py-5 flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-warn-600 mt-0.5 shrink-0" />
          <div className="text-sm text-warn-600">
            <p>{error || t('auth.error_verify_failed')}</p>
            <button
              onClick={handleResend}
              disabled={resending || resent}
              className="mt-3 text-xs font-semibold underline hover:text-warn-600 disabled:opacity-60"
            >
              {resent ? t('auth.resent') : resending ? t('common.loading') : t('auth.resend_verification')}
            </button>
          </div>
        </div>
      </AuthLayout>
    );
  }

  // state === 'awaiting' (post-signup, before user clicks the link)
  return (
    <AuthLayout
      title={t('auth.verify_awaiting_title')}
      subtitle={email ? t('auth.verify_awaiting_subtitle', { email }) : t('auth.verify_awaiting_generic')}
      footer={
        <>
          {t('auth.didnt_arrive')}{' '}
          <button
            onClick={handleResend}
            disabled={resending || resent}
            className="font-semibold text-accent-700 hover:underline disabled:opacity-60"
          >
            {resent ? t('auth.resent') : resending ? t('common.loading') : t('auth.resend_verification')}
          </button>
        </>
      }
    >
      <div className="rounded-2xl bg-accent-50 border border-accent-200 px-4 py-5 flex items-start gap-3">
        <Mail className="h-5 w-5 text-accent-700 mt-0.5 shrink-0" />
        <p className="text-sm text-accent-800">{t('auth.verify_awaiting_msg')}</p>
      </div>
      {error && (
        <p className="mt-3 text-xs text-warn-600">{error}</p>
      )}
      <p className="mt-6 text-xs text-ink-500">
        <Link to="/login" className="text-accent-700 hover:underline">
          {t('auth.skip_to_login')}
        </Link>
      </p>
    </AuthLayout>
  );
}
